import createDOMPurify from 'dompurify'

type SanitizerConfig = {
  ADD_ATTR?: string[]
  USE_PROFILES?: {
    html?: boolean
    svg?: boolean
    svgFilters?: boolean
    mathMl?: boolean
  }
}

type PurifierLike = {
  addHook?: (hookName: string, handler: (node: Element, data: { attrName?: string; attrValue?: string; keepAttr?: boolean }) => void) => void
  sanitize: (dirty: string, config?: SanitizerConfig) => string
}

const allowedAnchorTargets = new Set(['_blank'])

export const RICH_HTML_SANITIZER_CONFIG: SanitizerConfig = {
  ADD_ATTR: ['target', 'rel'],
  USE_PROFILES: {
    html: true,
    svg: true,
    svgFilters: true,
    mathMl: true,
  },
}

export const HTML_ONLY_SANITIZER_CONFIG: SanitizerConfig = {
  ADD_ATTR: ['target', 'rel'],
  USE_PROFILES: {
    html: true,
  },
}

export const SVG_ONLY_SANITIZER_CONFIG: SanitizerConfig = {
  USE_PROFILES: {
    svg: true,
    svgFilters: true,
  },
}

const purifier = (() => {
  if (typeof window !== 'undefined') {
    return createDOMPurify(window) as PurifierLike
  }
  const candidate = createDOMPurify as unknown as Partial<PurifierLike>
  if (typeof candidate.sanitize === 'function') {
    return candidate as PurifierLike
  }
  return null
})()

purifier?.addHook?.('uponSanitizeAttribute', (node, data) => {
  if (node.tagName !== 'A' || data.attrName !== 'target') return
  if (!data.attrValue || !allowedAnchorTargets.has(data.attrValue)) {
    data.keepAttr = false
  }
})

export function sanitizeHtmlFragment(dirty: string, config?: SanitizerConfig): string {
  if (!purifier) return dirty
  return purifier.sanitize(dirty, config)
}

export function sanitizeRichHtml(dirty: string): string {
  return sanitizeHtmlFragment(dirty, RICH_HTML_SANITIZER_CONFIG)
}

export function sanitizeHtmlOnly(dirty: string): string {
  return sanitizeHtmlFragment(dirty, HTML_ONLY_SANITIZER_CONFIG)
}

export function sanitizeSvgOnly(dirty: string): string {
  return sanitizeHtmlFragment(dirty, SVG_ONLY_SANITIZER_CONFIG)
}
